'use client';
import React, { useState, useEffect, useRef } from 'react';

const stats = [
  { value: '48h', label: 'Average time to initial interpretation' },
  { value: '94%', label: 'Of referrals receive a definitive diagnosis within 2 weeks' },
  { value: '12+', label: 'Years focused exclusively on hematologic disorders' },
];

export default function ReferralSection() {
  const [initials, setInitials] = useState('');
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!initials || !phone) return;
    setSubmitted(true);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="refer"
      ref={sectionRef}
      className="py-24 px-6"
      style={{ background: '#3B4856' }}
    >
      {/* Atmospheric glow */}
      <div
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          width: 600,
          height: 400,
          background: 'radial-gradient(ellipse, rgba(137,176,208,0.1) 0%, transparent 70%)',
          top: '30%',
        }}
      />

      <div className="max-w-6xl mx-auto relative">
        {/* Stats row */}
        <div className="reveal grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="text-center p-6 rounded-2xl"
              style={{
                background: 'rgba(137,176,208,0.06)',
                border: '1px solid rgba(137,176,208,0.15)',
              }}
            >
              <div
                className="font-serif text-5xl font-semibold mb-2"
                style={{ color: '#89B0D0' }}
              >
                {stat.value}
              </div>
              <p className="text-sm font-light leading-relaxed" style={{ color: 'rgba(244,247,250,0.6)' }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        <div className="reveal grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Copy */}
          <div>
            <span
              className="text-xs font-bold tracking-widest uppercase block mb-4"
              style={{ color: '#89B0D0' }}
            >
              Refer a Patient
            </span>
            <h2
              className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight mb-6"
              style={{ color: '#F4F7FA' }}
            >
              When the CBC
              <br />
              <span className="italic font-light" style={{ color: '#89B0D0' }}>
                Doesn't Add Up
              </span>
            </h2>
            <p
              className="text-lg font-light leading-relaxed mb-8"
              style={{ color: 'rgba(244,247,250,0.65)' }}
            >
              For the patient whose bruising won't stop. The man whose fatigue has outlasted every vitamin. The parents holding a newborn's CBC that
              doesn't look right. Send the referral — we'll take it from here.
            </p>
            <div className="space-y-4">
              {[
                'Urgent consultations acknowledged within 2 hours',
                'Direct physician-to-physician communication',
                'Detailed interpretation report returned to referring provider',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: 'rgba(196,91,106,0.2)', color: '#C45B6A' }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <span className="text-sm font-light" style={{ color: 'rgba(244,247,250,0.7)' }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Referral Form */}
          <div
            className="rounded-3xl p-8 md:p-10"
            style={{
              background: 'rgba(244,247,250,0.05)',
              border: '1px solid rgba(137,176,208,0.2)',
              backdropFilter: 'blur(12px)',
            }}
          >
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <h3
                    className="font-serif text-2xl mb-1"
                    style={{ color: '#F4F7FA' }}
                  >
                    Refer a Patient
                  </h3>
                  <p className="text-sm font-light" style={{ color: 'rgba(244,247,250,0.5)' }}>
                    Two fields. That's all we need to begin.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="initials"
                    className="block text-xs font-bold tracking-widest uppercase mb-2"
                    style={{ color: '#89B0D0' }}
                  >
                    Patient Initials
                  </label>
                  <input
                    id="initials"
                    type="text"
                    value={initials}
                    onChange={(e) => setInitials(e.target.value)}
                    placeholder="e.g. J.M."
                    maxLength={6}
                    className="form-input w-full px-4 py-3 rounded-xl text-sm font-medium"
                    style={{ background: 'rgba(244,247,250,0.08)', color: '#F4F7FA' }}
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="refphone"
                    className="block text-xs font-bold tracking-widest uppercase mb-2"
                    style={{ color: '#89B0D0' }}
                  >
                    Referring Physician Phone
                  </label>
                  <input
                    id="refphone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(212) 555-0192"
                    className="form-input w-full px-4 py-3 rounded-xl text-sm font-medium"
                    style={{ background: 'rgba(244,247,250,0.08)', color: '#F4F7FA' }}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn-rose w-full py-4 rounded-xl text-base font-semibold flex items-center justify-center gap-2.5"
                >
                  Submit Referral
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>

                <p className="text-xs text-center font-light" style={{ color: 'rgba(244,247,250,0.35)' }}>
                  HIPAA-compliant intake. We'll call you within 2 hours on business days.
                </p>
              </form>
            ) : (
              <div className="text-center py-8">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ background: 'rgba(196,91,106,0.15)', border: '1.5px solid #C45B6A' }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C45B6A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="font-serif text-2xl mb-3" style={{ color: '#F4F7FA' }}>
                  Referral received.
                </h3>
                <p className="text-sm font-light leading-relaxed" style={{ color: 'rgba(244,247,250,0.6)' }}>
                  We'll reach you at the number provided within 2 business hours.
                  Patient initials have been logged securely.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}